// src/hooks/usePendingAuthorizations.ts
import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import api from '../utils/axios';
import { decrypt } from '../utils/encryption';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4020';

export interface PendingAuthRequest {
  id: number;
  codigo: string;
  accion: {
    codigo: string;
    nombre: string;
    modulo: string;
  };
  solicitante: {
    id: number;
    nombre: string;
    usuario: string;
  };
  datosAccion: any;
  fechaSolicitud: string;
  fechaExpiracion: string;
  tiempoRestante: string;
}

interface UsePendingAuthResult {
  pendientes: PendingAuthRequest[];
  count: number;
  loading: boolean;
  approve: (codigo: string, observaciones?: string) => Promise<void>;
  reject: (codigo: string, observaciones?: string) => Promise<void>;
  refresh: () => Promise<void>;
  hasNewRequest: boolean;
  clearNewRequestFlag: () => void;
}

export const usePendingAuthorizations = (): UsePendingAuthResult => {
  const { user } = useAuth();
  const [pendientes, setPendientes] = useState<PendingAuthRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasNewRequest, setHasNewRequest] = useState(false);
  const previousCountRef = useRef(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Polling interval from env (default 30s)
  const POLL_INTERVAL_MS = parseInt(
    process.env.REACT_APP_AUTH_POLL_INTERVAL || '30',
    10,
  ) * 1000;

  const loadPendientes = useCallback(async () => {
    if (!user) return;
    try {
      // Una sola petición: el conteo se deriva del arreglo (antes se pedía
      // /permisos/pendientes/count por separado, era redundante).
      const resPendientes = await api.get('/permisos/pendientes');
      const nuevos: PendingAuthRequest[] = resPendientes.data || [];
      setPendientes(nuevos);

      // Detectar nueva solicitud comparando contra el conteo previo
      const countActual = nuevos.length;
      if (previousCountRef.current > 0 && countActual > previousCountRef.current) {
        setHasNewRequest(true);
      }
      previousCountRef.current = countActual;
    } catch {
      // Silenciar errores de polling
    }
  }, [user]);

  const approve = useCallback(async (codigo: string, observaciones?: string) => {
    await api.post(`/permisos/autorizar/${codigo}`, {
      autorizar: true,
      observaciones,
    });
    await loadPendientes();
  }, [loadPendientes]);

  const reject = useCallback(async (codigo: string, observaciones?: string) => {
    await api.post(`/permisos/autorizar/${codigo}`, {
      autorizar: false,
      observaciones,
    });
    await loadPendientes();
  }, [loadPendientes]);

  const refresh = useCallback(async () => {
    await loadPendientes();
  }, [loadPendientes]);

  const clearNewRequestFlag = useCallback(() => {
    setHasNewRequest(false);
  }, []);

  // Tiempo real vía SSE con respaldo a polling.
  // - SSE (fetch-event-source, envía el Bearer token) entrega las novedades al
  //   instante; mientras la conexión está viva NO se hace polling.
  // - Si la SSE falla o no conecta, se activa el polling como red de seguridad.
  // - El polling de respaldo se pausa cuando la pestaña está oculta.
  useEffect(() => {
    if (!user || user.Rol === 'Ventas' || user.Rol === 'Compras') return;

    const ctrl = new AbortController();
    let sseVivo = false;

    const startPoll = () => {
      if (pollRef.current || document.hidden) return;
      pollRef.current = setInterval(loadPendientes, POLL_INTERVAL_MS);
    };
    const stopPoll = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    // Carga inicial
    loadPendientes();

    // Conexión SSE
    let token = '';
    try {
      const enc = localStorage.getItem('token');
      if (enc) token = decrypt(enc);
    } catch { /* sin token válido: quedará solo el polling */ }

    if (token) {
      fetchEventSource(`${API_URL}/permisos/pendientes/stream`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: ctrl.signal,
        openWhenHidden: true,
        onopen: async () => {
          sseVivo = true;
          stopPoll(); // SSE en vivo → no hace falta polling
        },
        onmessage: (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (data?.tipo && data.tipo !== 'ping') loadPendientes();
          } catch { /* ignorar mensajes no-JSON */ }
        },
        onerror: () => {
          // fetch-event-source reintenta solo; entretanto activamos el respaldo
          sseVivo = false;
          startPoll();
          // devolver undefined => reintento con backoff automático
        },
      }).catch(() => { startPoll(); });
    } else {
      startPoll();
    }

    // Pausar/reanudar el polling de respaldo según visibilidad (la SSE se
    // mantiene sola gracias a openWhenHidden).
    const onVisibility = () => {
      if (document.hidden) stopPoll();
      else if (!sseVivo) startPoll();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      ctrl.abort();
      stopPoll();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, loadPendientes, POLL_INTERVAL_MS]);

  return {
    pendientes,
    count: pendientes.length,
    loading,
    approve,
    reject,
    refresh,
    hasNewRequest,
    clearNewRequestFlag,
  };
};
